import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddressMismatchCheck } from '../model/addressMismatchFlow';

const checkKey = (tracking: string) => ['address-mismatch-report', tracking] as const;

// Whether this tracking was already reported to Amazon's sheet — the only thing that decides if
// «Notificar mismatch» is offered at all. The host answers it (it holds the Google token, and reads
// the sheet only when a token is already there: the check never opens Google's popup outside a
// click), and the send teaches it too, through `markReported`.
//
// The cache IS the session's memory: a «reported» answer never goes stale, so an «Entrega» opened
// again after a send (or a duplicate) shows no button even when the sheet cannot be read without a
// token. «Not reported» does go stale, so the next opening asks again — by then the agent may hold
// a token. A failed check reads as «not reported»: a lost report is worse than a repeated one, and
// the send checks the sheet again before it writes.
export function useAddressMismatchCheck(
  tracking: string,
  check: ((tracking: string) => Promise<AddressMismatchCheck>) | undefined,
  enabled: boolean,
): {
  checking: boolean;
  alreadyReported: boolean;
  markReported: () => void;
} {
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: checkKey(tracking),
    queryFn: () => (check ? check(tracking) : Promise.resolve({ reported: false } as const)),
    enabled: enabled && tracking !== '',
    staleTime: (query) => (query.state.data?.reported ? Infinity : 0),
    refetchOnWindowFocus: false,
    retry: false,
    throwOnError: false,
  });
  const markReported = () => {
    queryClient.setQueryData<AddressMismatchCheck>(checkKey(tracking), { reported: true });
  };
  return {
    // A disabled query stays pending forever; only an enabled one is «checking».
    checking: enabled && tracking !== '' && isPending,
    alreadyReported: data?.reported === true,
    markReported,
  };
}
